import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

// Token storage (in-memory for security, but accessible to axios interceptor)
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (authToken && config.headers) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    // Don't auto-redirect on 401 - let the auth context handle it
    return Promise.reject(error);
  }
);

export default apiClient;

// Type definitions for API responses
export interface Lead {
  id: number;
  source_id: number;
  external_id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  region: string;
  phone: string | null;
  cuisine_raw: string | null;
  cuisine_detected: string | null;
  cuisine_keywords: string[] | null;
  license_type: string | null;
  license_status: string | null;
  inspection_date: string | null;
  effective_date: string | null;
  is_new: boolean;
  first_seen_at: string | null;
  status: 'new' | 'contacted' | 'qualified' | 'closed';
  notes: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  source_name?: string;
  source_region?: string;
  source_type?: string;
  // Automation fields
  score?: number | null;
  score_breakdown?: Record<string, unknown> | null;
  last_scored_at?: string | null;
  auto_disqualified?: boolean;
  disqualify_reason?: string | null;
  status_changed_at?: string | null;
  sla_warning_sent_at?: string | null;
  sla_escalated_at?: string | null;
}

export interface LeadListResponse {
  leads: Lead[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LeadStats {
  total: number;
  new_count: number;
  status_new: number;
  status_contacted: number;
  status_qualified: number;
  status_closed: number;
  by_region: Array<{ region: string; total: number; new_count: number }>;
  by_cuisine: Array<{ cuisine: string; total: number; new_count: number }>;
}

export interface DataSource {
  id: number;
  name: string;
  region: string;
  source_type: string;
  api_url: string;
  active: boolean;
  last_fetch_at: string | null;
}

export interface FetchRun {
  id: number;
  source_id: number;
  started_at: string;
  completed_at: string | null;
  status: 'running' | 'success' | 'failed';
  total_records: number | null;
  new_records: number | null;
  updated_records: number | null;
  error_message: string | null;
}

export interface LeadActivity {
  id: number;
  lead_id: number;
  user_email: string;
  action: string;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

export interface LeadFilters {
  region?: string;
  cuisine?: string;
  source?: string;
  is_new?: boolean;
  status?: string;
  search?: string;
  assigned_to?: string;
  min_score?: number;
  max_score?: number;
  sla_status?: 'ok' | 'warning' | 'escalated';
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// API functions
export const leadsApi = {
  getLeads: async (filters: LeadFilters = {}): Promise<LeadListResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });
    const response = await apiClient.get<LeadListResponse>(`/api/leads?${params}`);
    return response.data;
  },

  getLead: async (id: number): Promise<Lead> => {
    const response = await apiClient.get<Lead>(`/api/leads/${id}`);
    return response.data;
  },

  updateLead: async (id: number, data: Partial<Lead>): Promise<Lead> => {
    const response = await apiClient.patch<Lead>(`/api/leads/${id}`, data);
    return response.data;
  },

  assignLead: async (id: number, assignedTo: string): Promise<Lead> => {
    const response = await apiClient.patch<Lead>(`/api/leads/${id}/assign`, { assigned_to: assignedTo });
    return response.data;
  },

  getStats: async (): Promise<LeadStats> => {
    const response = await apiClient.get<LeadStats>('/api/leads/stats');
    return response.data;
  },

  getActivities: async (id: number): Promise<{ activities: LeadActivity[] }> => {
    const response = await apiClient.get<{ activities: LeadActivity[] }>(`/api/leads/${id}/activities`);
    return response.data;
  },

  claimLead: async (id: number): Promise<Lead> => {
    const response = await apiClient.post<Lead>(`/api/leads/${id}/claim`);
    return response.data;
  },

  releaseLead: async (id: number): Promise<Lead> => {
    const response = await apiClient.post<Lead>(`/api/leads/${id}/release`);
    return response.data;
  },
};

// User and Role types
export type UserRole = 'superadmin' | 'sales_manager' | 'exec' | 'sales' | 'asst' | 'viewer' | 'unauthorized';

export const ALL_ROLES: UserRole[] = ['superadmin', 'sales_manager', 'exec', 'sales', 'asst', 'viewer'];

export type Permission =
  | 'dashboard:view'
  | 'dashboard:edit'
  | 'leads:view'
  | 'leads:view_assignments'
  | 'leads:claim'
  | 'leads:update_status'
  | 'leads:assign'
  | 'leads:approve_request'
  | 'leads:return_to_pool'
  | 'sources:view'
  | 'sources:fetch'
  | 'admin:impersonate'
  | 'admin:manage_users'
  | 'automation:view'
  | 'automation:configure';

export interface UserInfo {
  email: string;
  name: string;
  role: UserRole;
  permissions: Record<Permission, boolean>;
}

export const userApi = {
  getMe: async (): Promise<UserInfo> => {
    const response = await apiClient.get<UserInfo>('/api/user/me');
    return response.data;
  },

  getSalesUsers: async (): Promise<{ users: string[] }> => {
    const response = await apiClient.get<{ users: string[] }>('/api/user/sales');
    return response.data;
  },
};

export const sourcesApi = {
  getSources: async (): Promise<{ sources: DataSource[] }> => {
    const response = await apiClient.get('/api/sources');
    return response.data;
  },

  getSource: async (id: number): Promise<DataSource> => {
    const response = await apiClient.get<DataSource>(`/api/sources/${id}`);
    return response.data;
  },

  getRuns: async (id: number): Promise<{ runs: FetchRun[] }> => {
    const response = await apiClient.get(`/api/sources/${id}/runs`);
    return response.data;
  },

  triggerFetch: async (id: number): Promise<unknown> => {
    const response = await apiClient.post(`/api/fetch/${id}`);
    return response.data;
  },

  triggerFetchAll: async (): Promise<unknown> => {
    const response = await apiClient.post('/api/fetch/all');
    return response.data;
  },
};

// Admin types
export interface UserRoleRecord {
  email: string;
  role: UserRole;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PermissionsMatrix {
  matrix: Record<UserRole, Record<Permission, boolean>>;
  permissions: Permission[];
  roles: UserRole[];
}

export const adminApi = {
  getUsers: async (): Promise<{ users: UserRoleRecord[] }> => {
    const response = await apiClient.get<{ users: UserRoleRecord[] }>('/api/admin/users');
    return response.data;
  },

  setUserRole: async (email: string, role: UserRole): Promise<{ user: UserRoleRecord }> => {
    const response = await apiClient.post<{ user: UserRoleRecord }>('/api/admin/users', {
      email,
      role,
    });
    return response.data;
  },

  deleteUserRole: async (email: string): Promise<void> => {
    await apiClient.delete(`/api/admin/users/${encodeURIComponent(email)}`);
  },

  getPermissions: async (): Promise<PermissionsMatrix> => {
    const response = await apiClient.get<PermissionsMatrix>('/api/admin/permissions');
    return response.data;
  },

  updatePermission: async (
    role: UserRole,
    permission: Permission,
    granted: boolean
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      '/api/admin/permissions',
      { role, permission, granted }
    );
    return response.data;
  },

  getRoles: async (): Promise<{ roles: UserRole[] }> => {
    const response = await apiClient.get<{ roles: UserRole[] }>('/api/admin/roles');
    return response.data;
  },
};

// Automation types
export interface AutomationConfig {
  id: number;
  key: string;
  value: unknown;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScoringWeights {
  location: number;
  cuisine: number;
  license: number;
  freshness: number;
}

export interface TargetCuisines {
  [cuisine: string]: number;
}

export interface SLAStatusConfig {
  warning_hours: number;
  escalation_hours: number;
  return_hours: number | null;
  return_enabled: boolean;
}

export interface SLAConfig {
  new: SLAStatusConfig;
  contacted: SLAStatusConfig;
  qualified: SLAStatusConfig;
}

export interface RoundRobinConfig {
  enabled: boolean;
  working_hours_only: boolean;
  working_hours: {
    start: number;
    end: number;
    timezone: string;
  };
  exclude_weekends: boolean;
  max_leads_per_rep_per_day: number;
}

export interface DisqualificationRule {
  enabled: boolean;
  action: 'flag' | 'disqualify';
  description: string;
  statuses?: string[];
  match_fields?: string[];
}

export interface DisqualificationRules {
  non_asian_cuisine: DisqualificationRule;
  license_issues: DisqualificationRule;
  duplicate_detection: DisqualificationRule;
  blacklist_match: DisqualificationRule;
}

export interface BlacklistEntry {
  id: number;
  entry_type: 'name' | 'address' | 'phone' | 'domain';
  pattern: string;
  match_type: 'exact' | 'contains' | 'regex';
  reason: string | null;
  created_by: string | null;
  created_at: string;
  is_active: boolean;
}

export interface TargetLocation {
  id: number;
  location_type: string;
  value: string;
  score_boost: number;
  is_active: boolean;
  created_at: string;
}

export interface ScoreStats {
  total: number;
  scored: number;
  average: number;
  distribution: { range: string; count: number }[];
}

export interface SLASummary {
  total_assigned: number;
  ok_count: number;
  warning_count: number;
  escalated_count: number;
  by_status: { status: string; ok: number; warning: number; escalated: number }[];
  by_rep: { rep: string; ok: number; warning: number; escalated: number }[];
}

export const automationApi = {
  // Config
  getConfigs: async (): Promise<{ configs: AutomationConfig[] }> => {
    const response = await apiClient.get<{ configs: AutomationConfig[] }>('/api/automation/config');
    return response.data;
  },

  getConfig: async <T>(key: string): Promise<{ key: string; value: T }> => {
    const response = await apiClient.get<{ key: string; value: T }>(`/api/automation/config/${key}`);
    return response.data;
  },

  setConfig: async <T>(key: string, value: T, description?: string): Promise<{ success: boolean; config: AutomationConfig }> => {
    const response = await apiClient.put<{ success: boolean; config: AutomationConfig }>(
      `/api/automation/config/${key}`,
      { value, description }
    );
    return response.data;
  },

  // Scoring
  scoreLead: async (leadId: number): Promise<{ success: boolean; leadId: number; breakdown: unknown }> => {
    const response = await apiClient.post<{ success: boolean; leadId: number; breakdown: unknown }>(
      `/api/automation/score/${leadId}`
    );
    return response.data;
  },

  scoreAllLeads: async (batchSize?: number): Promise<{ success: boolean; scored: number; errors: number }> => {
    const response = await apiClient.post<{ success: boolean; scored: number; errors: number }>(
      '/api/automation/score/all',
      { batchSize }
    );
    return response.data;
  },

  getScoreStats: async (): Promise<ScoreStats> => {
    const response = await apiClient.get<ScoreStats>('/api/automation/score/stats');
    return response.data;
  },

  // SLA
  getSLAStatus: async (): Promise<SLASummary> => {
    const response = await apiClient.get<SLASummary>('/api/automation/sla/status');
    return response.data;
  },

  getSLAIssues: async (assignedTo?: string): Promise<{ leads: Lead[] }> => {
    const params = assignedTo ? `?assigned_to=${encodeURIComponent(assignedTo)}` : '';
    const response = await apiClient.get<{ leads: Lead[] }>(`/api/automation/sla/issues${params}`);
    return response.data;
  },

  triggerSLACheck: async (): Promise<{ success: boolean; results: unknown }> => {
    const response = await apiClient.post<{ success: boolean; results: unknown }>('/api/automation/sla/check');
    return response.data;
  },

  // Round Robin
  getAssignmentStats: async (): Promise<{ total_auto_assigned: number; today_assigned: number; by_rep: { rep: string; count: number }[] }> => {
    const response = await apiClient.get('/api/automation/assignment/stats');
    return response.data;
  },

  getAssignmentStatus: async (): Promise<{ should: boolean; reason?: string; config: RoundRobinConfig }> => {
    const response = await apiClient.get('/api/automation/assignment/status');
    return response.data;
  },

  // Blacklist
  getBlacklist: async (): Promise<{ entries: BlacklistEntry[] }> => {
    const response = await apiClient.get<{ entries: BlacklistEntry[] }>('/api/automation/blacklist');
    return response.data;
  },

  addBlacklistEntry: async (
    entry: Omit<BlacklistEntry, 'id' | 'created_by' | 'created_at' | 'is_active'>
  ): Promise<{ success: boolean; entry: BlacklistEntry }> => {
    const response = await apiClient.post<{ success: boolean; entry: BlacklistEntry }>(
      '/api/automation/blacklist',
      entry
    );
    return response.data;
  },

  deleteBlacklistEntry: async (id: number): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/api/automation/blacklist/${id}`);
    return response.data;
  },

  getBlacklistStats: async (): Promise<{ total: number; active: number; by_type: { type: string; count: number }[]; recent_matches: number }> => {
    const response = await apiClient.get('/api/automation/blacklist/stats');
    return response.data;
  },

  // Qualification
  getQualificationStats: async (): Promise<{ total_disqualified: number; total_flagged: number; disqualified_today: number; flagged_today: number; by_reason: { reason: string; count: number }[] }> => {
    const response = await apiClient.get('/api/automation/qualification/stats');
    return response.data;
  },

  evaluateLead: async (leadId: number): Promise<{ success: boolean; evaluation: unknown; processResult: unknown }> => {
    const response = await apiClient.post(`/api/automation/qualification/evaluate/${leadId}`);
    return response.data;
  },

  // Target Locations
  getTargetLocations: async (): Promise<{ locations: TargetLocation[] }> => {
    const response = await apiClient.get<{ locations: TargetLocation[] }>('/api/automation/locations');
    return response.data;
  },

  setTargetLocation: async (
    location: Omit<TargetLocation, 'id' | 'created_at'>
  ): Promise<{ success: boolean; location: TargetLocation }> => {
    const response = await apiClient.post<{ success: boolean; location: TargetLocation }>(
      '/api/automation/locations',
      location
    );
    return response.data;
  },

  deleteTargetLocation: async (id: number): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/api/automation/locations/${id}`);
    return response.data;
  },
};
